import React, { useState, useEffect } from 'react';
import { collection, query, getDocs, doc, getDoc, orderBy, limit } from 'firebase/firestore';
import { db } from './firebase';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

import Hero from './Hero';
import GlassCard from './GlassCard'; 
import ProductModal from './ProductModal';
import WhatsAppButton from './WhatsAppButton';
import FAQSection from './FAQSection';
import Footer from './Footer';

const SkeletonCard = () => (
  <div className="bg-gray-100 border border-gray-200 rounded-[2.5rem] p-4 animate-pulse">
    <div className="w-full aspect-square bg-gray-200 rounded-[2rem] mb-4" />
    <div className="h-4 bg-gray-200 rounded-full w-3/4 mb-2" />
  </div>
);

const PublicSite = () => {
  const navigate = useNavigate();
  const [latestProducts, setLatestProducts] = useState([]);
  const [faqs, setFaqs] = useState([]); 
  const [heroSlides, setHeroSlides] = useState([]);
  const [whatsapp, setWhatsapp] = useState('');
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);

  const fetchData = async () => {
    try {
      const configSnap = await getDoc(doc(db, "settings", "home"));
      if (configSnap.exists()) {
        const data = configSnap.data();
        setHeroSlides(data.heroSlides || []);
        setWhatsapp(data.whatsappNumber || '');
        setFaqs(data.faqs || []);
      }
      const qLatest = query(collection(db, "products"), orderBy("name", "desc"), limit(10));
      const snapLatest = await getDocs(qLatest);
      setLatestProducts(snapLatest.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  return (
    <div className="flex flex-col min-h-screen bg-white text-slate-900 font-sans selection:bg-[#a6192e] selection:text-white overflow-x-hidden">
      <Hero slides={heroSlides} loading={loading} />
      <main className="flex-1 bg-white relative z-10">
        <section id="novedades" className="py-20 px-6 max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-end mb-12 gap-6">
            <div className="space-y-2">
              <span className="text-[#a6192e] font-black text-xs tracking-[0.4em] uppercase italic">Catálogo Reciente</span>
              <h2 className="text-4xl md:text-6xl font-black italic tracking-tighter uppercase leading-none">
                Nuestras <span className="text-[#a6192e]">Novedades</span>
              </h2>
            </div>
            <button onClick={() => navigate('/productos')} className="text-[10px] font-black text-[#a6192e] border-b-2 border-[#a6192e]/20 pb-1 uppercase tracking-widest">
              Ver catálogo completo ➔
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
            {loading ? (
              Array(5).fill(0).map((_, i) => <SkeletonCard key={i} />)
            ) : (
              latestProducts.map((p, i) => (
                <motion.div 
                  key={p.id} 
                  initial={{ opacity: 0, y: 20 }} 
                  whileInView={{ opacity: 1, y: 0 }} 
                  viewport={{ once: true, amount: 0.1 }}
                >
                  <GlassCard item={p} onOpen={() => {setSelectedProduct(p); setIsModalOpen(true);}} />
                </motion.div>
              ))
            )}
          </div>
        </section>

        {!loading && faqs.length > 0 && (
          <FAQSection faqs={faqs} whatsappNumber={whatsapp} />
        )}
      </main>

      <AnimatePresence>
        {isModalOpen && (
          <ProductModal 
            isOpen={isModalOpen} 
            onClose={() => setIsModalOpen(false)} 
            product={selectedProduct}
            whatsappNumber={whatsapp}
          />
        )}
      </AnimatePresence>

      <WhatsAppButton whatsappNumber={whatsapp} />
    </div>
  );
};

export default PublicSite;